import {useLazyQuery} from '@apollo/client'
import axios from 'axios'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Alert, Badge, Button, CloseButton, Form} from 'react-bootstrap'
import {toast} from 'react-toastify'

import {
  TELEPHONY_DRAWER_CLOSE_EVENT,
  TELEPHONY_DRAWER_OPEN_EVENT,
  TELEPHONY_DRAWER_STATE_EVENT,
  TELEPHONY_DRAWER_STATE_REQUEST_EVENT,
  TELEPHONY_DRAWER_TOGGLE_EVENT,
  TELEPHONY_DRAWER_UPDATE_EVENT,
  TelephonyDrawerOpenDetail,
  TelephonyDrawerDiagnostics,
  TelephonyDrawerStateDetail,
  TELEPHONY_VICIDIAL_LOGOUT_EVENT,
  TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT,
  TelephonyVicidialPrepareDialDetail,
} from '../../types/telephony'
import {URL} from '../../gql/client'
import {GET_ALL_CONTACTS_PREVIEW} from '../../gql/queries/contactsApiQueries'
import {GET_ONE_LEAD} from '../../gql/queries/leadsQuery'
import {STORAGE_NAMES} from '../../helpers/consts'
import {useTelephony} from '../../hooks/useTelephony'
import useWebphoneOwnership from '../../hooks/useWebphoneOwnership'
import {IResContactsPreviewAPI, MemberPolicySummary, NodeContact} from '../../types/contactsApi'
import AudioDeviceCheck from './AudioDeviceCheck'
import './TelephonyDrawer.css'

const VICIDIAL_EXTERNAL_WINDOW_KEY = '__policytoolsVicidialWebphoneWindow'
const VICIDIAL_DRAWER_STANDALONE_QUERY_PARAM = 'vicidial_drawer'

const hasMeaningfulVicidialValue = (value?: string | number | null) => {
  const normalized = String(value || '')
    .trim()
    .toUpperCase()
  return Boolean(normalized && !['0', 'NONE', 'NULL', 'N/A'].includes(normalized))
}

const normalizePhoneDigits = (value?: string | number | null) =>
  String(value || '').replace(/\D+/g, '')

const phonesLikelyMatch = (left?: string | number | null, right?: string | number | null) => {
  const normalizedLeft = normalizePhoneDigits(left)
  const normalizedRight = normalizePhoneDigits(right)
  if (!normalizedLeft || !normalizedRight) return false
  if (normalizedLeft === normalizedRight) return true

  const localLeft = normalizedLeft.slice(-10)
  const localRight = normalizedRight.slice(-10)
  return Boolean(localLeft && localRight && localLeft === localRight)
}

const pickBestPolicyToolsContact = (
  contacts: NodeContact[],
  targetPhone: string
): NodeContact | null => {
  if (!contacts.length) return null
  const exactPhoneMatch = contacts.find((contact) => phonesLikelyMatch(contact.phone, targetPhone))
  return exactPhoneMatch || contacts[0] || null
}

const formatPolicyToolsLocation = (contact?: NodeContact | null) => {
  if (!contact) return ''
  return [contact.city, contact.stateEU?.name].filter(Boolean).join(', ')
}

const formatElapsedClock = (elapsedMs: number) => {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
      seconds
    ).padStart(2, '0')}`
  }

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const buildPolicyToolsPolicyLabel = (memberPolicy?: MemberPolicySummary | null) => {
  const policy = memberPolicy?.policy
  if (!policy) return ''

  const baseLabel =
    policy.policyName ||
    policy.typePolicy?.typePolicy ||
    policy.type ||
    policy.carrier?.name ||
    'Policy'
  const statusLabel = policy.status || policy.paymentStatus || ''
  return [baseLabel, statusLabel].filter(Boolean).join(' • ')
}

const computePolicyToolsAge = (dateBirth?: string | null): number | null => {
  if (!dateBirth) return null
  const dob = new Date(dateBirth)
  if (Number.isNaN(dob.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - dob.getFullYear()
  const monthDiff = now.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age -= 1
  return age >= 0 && age < 130 ? age : null
}

const policyToolsStatusTone = (status: string): 'completed' | 'pending' | 'neutral' => {
  const normalized = status.toUpperCase()
  if (/COMPLET|PROCES|ACTIV|APPROV|PAGAD/.test(normalized)) return 'completed'
  if (/PEND/.test(normalized)) return 'pending'
  return 'neutral'
}

const formatPolicyToolsPersonName = (
  person?: {firstName?: string | null; lastName?: string | null} | null
): string =>
  person
    ? [person.firstName, person.lastName]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ')
    : ''

const isDispositionPendingState = (status?: string | null, realTimeSubStatus?: string | null) => {
  const statusValue = String(status || '')
    .trim()
    .toUpperCase()
  const subStatusValue = String(realTimeSubStatus || '')
    .trim()
    .toUpperCase()
  return (
    statusValue === 'DEAD' ||
    subStatusValue === 'DEAD' ||
    subStatusValue.includes('DEAD') ||
    statusValue === 'DISPO' ||
    subStatusValue === 'DISPO'
  )
}

// Audible incoming-call alert (Web Audio — no asset needed). The agent has already
// interacted with the page (login/dial), so the AudioContext is allowed to play.
const playIncomingCallAlert = () => {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    const ring = (offset: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = 1000
      osc.connect(gain)
      gain.connect(ctx.destination)
      const t = ctx.currentTime + offset
      gain.gain.setValueAtTime(0.0001, t)
      gain.gain.exponentialRampToValueAtTime(0.3, t + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4)
      osc.start(t)
      osc.stop(t + 0.42)
    }
    ring(0)
    ring(0.5)
    ring(1.0)
    window.setTimeout(() => ctx.close().catch(() => {}), 2200)
  } catch {
    // best-effort — ignore audio errors
  }
}

interface VicidialStandaloneDrawerSnapshot {
  drawerState: TelephonyDrawerOpenDetail
  embeddedWebphoneUrl?: string
  pauseCodeInput?: string
  manualPhoneNumber?: string
  manualLeadId?: number | string | null
}

const SIGNAL_BAR_FILL: Record<'good' | 'fair' | 'poor', number> = {good: 4, fair: 2, poor: 1}

// Cell-signal style bars for the agent's connection quality:
// good = 4 green bars, fair = 2 yellow bars, poor = 1 red bar.
const SignalBars = ({quality}: {quality: 'good' | 'fair' | 'poor'}) => {
  const filled = SIGNAL_BAR_FILL[quality] ?? 1
  return (
    <span className={`telephony-signal telephony-signal--${quality}`} aria-hidden='true'>
      {[1, 2, 3, 4].map((bar) => (
        <span key={bar} className={`telephony-signal__bar${bar <= filled ? ' is-on' : ''}`} />
      ))}
    </span>
  )
}

export const TelephonyDrawer = () => {
  const telephony = useTelephony()
  const [show, setShow] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [showAudioCheck, setShowAudioCheck] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [embeddedWebphoneUrl, setEmbeddedWebphoneUrl] = useState('')
  const [pauseCodeInput, setPauseCodeInput] = useState('')
  const [manualPhoneNumber, setManualPhoneNumber] = useState('')
  const [manualLeadId, setManualLeadId] = useState<number | string | null>(null)
  const [manualAutoDialPending, setManualAutoDialPending] = useState(false)
  const [transferPhoneNumber, setTransferPhoneNumber] = useState('')
  const [runningAction, setRunningAction] = useState('')
  const [nowTick, setNowTick] = useState(() => Date.now())
  const [policyToolsContact, setPolicyToolsContact] = useState<NodeContact | null>(null)
  const [policyToolsLead, setPolicyToolsLead] = useState<{
    id?: string
    name?: string
    phone?: string
    email?: string
    contact?: {firstName?: string; lastName?: string; phone?: string; email?: string} | null
  } | null>(null)
  const wasPausedRef = useRef(false)
  const preparedDialLifecycleSeenRef = useRef(false)
  const policyToolsLookupPhoneRef = useRef('')
  const policyToolsLookupLeadIdRef = useRef('')
  const wasInCallRef = useRef(false)
  // Timestamp when the agent's channel went without measurable media, so we only
  // warn about "no audio connection" after it's been sustained (avoids false alarms
  // on transient nulls right after login / between poll cycles).
  const noMediaSinceRef = useRef<number | null>(null)
  const [lookupPolicyToolsContact] = useLazyQuery<IResContactsPreviewAPI>(
    GET_ALL_CONTACTS_PREVIEW,
    {
      fetchPolicy: 'no-cache',
    }
  )
  const [lookupPolicyToolsLead] = useLazyQuery(GET_ONE_LEAD, {
    fetchPolicy: 'no-cache',
  })

  const wrapUpStartedAt = telephony.vicidialSession.wrapUpStartedAt
  const liveCallStartedAt = telephony.vicidialSession.callStartedAt
  useEffect(() => {
    if (!wrapUpStartedAt && !liveCallStartedAt) return
    setNowTick(Date.now())
    const id = window.setInterval(() => setNowTick(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [liveCallStartedAt, wrapUpStartedAt])

  // Alert the agent the moment a call lands — audible ring + toast + tab-title flash
  // — so they engage immediately and the customer doesn't hit dead air and hang up.
  const prevCallAlertRef = useRef<number | null>(null)
  useEffect(() => {
    const cur = liveCallStartedAt
    if (cur && cur !== prevCallAlertRef.current) {
      prevCallAlertRef.current = cur
      // Only for a genuinely fresh arrival, not a call already in progress on reload.
      if (Date.now() - cur < 8000) {
        playIncomingCallAlert()
        toast.info('📞 Incoming call — please answer', {
          autoClose: 6000,
          toastId: 'incoming-call',
        })
        const original = document.title
        document.title = '📞 INCOMING CALL'
        window.setTimeout(() => {
          if (document.title === '📞 INCOMING CALL') document.title = original
        }, 6000)
      }
    } else if (!cur) {
      prevCallAlertRef.current = null
    }
  }, [liveCallStartedAt])
  const [drawerState, setDrawerState] = useState<TelephonyDrawerOpenDetail>({
    url: '',
    title: 'Webphone',
  })

  const isVicidialAutologinBridge = useMemo(() => {
    return (
      drawerState.provider === 'VICIDIAL' &&
      drawerState.url.includes('/api/telephony/vicidial/autologin')
    )
  }, [drawerState.provider, drawerState.url])

  const shouldForceExternalOpen = useMemo(() => {
    if (!drawerState.url || typeof window === 'undefined') return false
    return window.location.protocol !== 'https:' && drawerState.url.startsWith('https://')
  }, [drawerState.url])

  const isAgentScreenMode = useMemo(() => {
    return drawerState.provider === 'VICIDIAL' && drawerState.displayMode === 'agent'
  }, [drawerState.displayMode, drawerState.provider])

  const canOperateVicidial = useMemo(() => {
    return (
      drawerState.provider === 'VICIDIAL' &&
      !isAgentScreenMode &&
      telephony.config.vicidial_enabled &&
      Boolean(telephony.config.vicidial_agent_user)
    )
  }, [
    drawerState.provider,
    isAgentScreenMode,
    telephony.config.vicidial_agent_user,
    telephony.config.vicidial_enabled,
  ])

  const publishDrawerState = useCallback(
    (mounted: boolean, visible: boolean, provider?: TelephonyDrawerStateDetail['provider']) => {
      window.dispatchEvent(
        new CustomEvent<TelephonyDrawerStateDetail>(TELEPHONY_DRAWER_STATE_EVENT, {
          detail: {
            mounted,
            visible,
            provider,
          },
        })
      )
    },
    []
  )

  const openInNewTab = () => {
    if (drawerState.provider === 'VICIDIAL' && drawerState.url) {
      const snapshot: VicidialStandaloneDrawerSnapshot = {
        drawerState,
        embeddedWebphoneUrl,
        pauseCodeInput,
        manualPhoneNumber,
        manualLeadId,
      }
      localStorage.setItem(STORAGE_NAMES.VICIDIAL_DRAWER_STANDALONE_STATE, JSON.stringify(snapshot))

      const targetLocation = new window.URL(window.location.href)
      targetLocation.searchParams.set(VICIDIAL_DRAWER_STANDALONE_QUERY_PARAM, '1')
      const externalWindow = window.open(targetLocation.toString(), '_blank')
      externalWindow?.focus?.()
      return
    }

    const targetUrl = isVicidialAutologinBridge
      ? `${drawerState.url}${drawerState.url.includes('?') ? '&' : '?'}standalone=1`
      : embeddedWebphoneUrl || drawerState.url
    if (!targetUrl) return
    const externalWindow = window.open(
      targetUrl,
      isVicidialAutologinBridge ? '_blank' : 'PolicyToolsVicidialWebphone'
    )
    if (externalWindow) {
      if (!isVicidialAutologinBridge) {
        ;(window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] = externalWindow
      }
      externalWindow.focus?.()
    }
  }

  const closeTrackedExternalWindow = () => {
    let externalWindow = (window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] as Window | null | undefined
    if (!externalWindow) {
      externalWindow = window.open('', 'PolicyToolsVicidialWebphone')
    }
    if (
      externalWindow &&
      !externalWindow.closed &&
      externalWindow.location.href !== 'about:blank'
    ) {
      externalWindow.close()
    }
    ;(window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] = null
  }

  const handleVicidialLogout = async () => {
    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    if (!token) {
      toast.error('Your session expired. Log in again to disconnect PolicyDial.')
      return
    }

    setDisconnecting(true)
    try {
      await axios.post(
        `${URL}/api/telephony/disconnect-agent`,
        {provider: 'VICIDIAL'},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
      localStorage.setItem(STORAGE_NAMES.VICIDIAL_FORCE_DISCONNECTED, '1')
      window.dispatchEvent(new CustomEvent(TELEPHONY_VICIDIAL_LOGOUT_EVENT))

      const channel = new BroadcastChannel('policytools_vicidial_channel')
      channel.postMessage('CLOSE_WEBPHONE')
      channel.close()

      closeTrackedExternalWindow()
      setCollapsed(false)
      setShow(false)
      toast.info(
        shouldForceExternalOpen
          ? 'PolicyDial disconnected. If the secure PolicyDial page still remains open in another browser tab, use the native LOGOUT link there once.'
          : 'PolicyDial disconnected.'
      )
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Unable to log out from PolicyDial.'
      toast.error(message)
    } finally {
      setDisconnecting(false)
    }
  }

  const runVicidialDrawerAction = async (actionKey: string, action: () => Promise<unknown>) => {
    setRunningAction(actionKey)
    try {
      await action()
    } finally {
      setRunningAction('')
    }
  }

  const handlePauseAgent = async () => {
    if (!pauseCodeInput.trim()) {
      toast.error('Select a pause reason before pausing.')
      return
    }
    await runVicidialDrawerAction('pause', () => telephony.setVicidialPause(true, pauseCodeInput))
  }

  const handleResumeAgent = async () => {
    await runVicidialDrawerAction('resume', () => telephony.setVicidialPause(false))
    // Reset the reason so the next pause forces a fresh, conscious selection.
    setPauseCodeInput('')
  }

  const handleTransferCall = async () => {
    const phoneNumber = transferPhoneNumber.trim()
    if (!phoneNumber) {
      toast.error('Enter a transfer phone number first.')
      return
    }

    const response = await telephony.transferVicidialCall(phoneNumber)
    if (response) {
      setTransferPhoneNumber('')
    }
  }

  const handleHangupCurrentCall = async () => {
    await runVicidialDrawerAction('hangup', () =>
      telephony.hangupCall('VICIDIAL', telephony.vicidialSession.localCallId || null)
    )
  }

  const handleManualCall = useCallback(async () => {
    const phoneNumber = manualPhoneNumber.trim()
    if (!phoneNumber) {
      toast.error('Enter a phone number first.')
      return
    }

    await runVicidialDrawerAction('dial', async () => {
      await telephony.startCall({
        phone: phoneNumber,
        leadId: manualLeadId,
        providerOverride: 'VICIDIAL',
        autoOpenWebphone: false,
      })
    })
  }, [manualLeadId, manualPhoneNumber, telephony])

  useEffect(() => {
    const currentStatus = String(telephony.vicidialSession.status || '')
      .trim()
      .toUpperCase()
    const currentPauseCode = String(telephony.vicidialSession.pauseCode || '')
      .trim()
      .toUpperCase()
    const currentlyPaused = currentStatus === 'PAUSED'

    if (currentPauseCode) {
      setPauseCodeInput(currentPauseCode)
    } else if (wasPausedRef.current && !currentlyPaused) {
      setPauseCodeInput('')
    }

    wasPausedRef.current = currentlyPaused
  }, [telephony.vicidialSession.pauseCode, telephony.vicidialSession.status])

  useEffect(() => {
    const isStandaloneDrawerView =
      new URLSearchParams(window.location.search).get(VICIDIAL_DRAWER_STANDALONE_QUERY_PARAM) ===
      '1'
    if (isStandaloneDrawerView) {
      const storedSnapshot = localStorage.getItem(STORAGE_NAMES.VICIDIAL_DRAWER_STANDALONE_STATE)
      if (storedSnapshot) {
        try {
          const snapshot = JSON.parse(storedSnapshot) as VicidialStandaloneDrawerSnapshot
          if (snapshot?.drawerState?.url) {
            setEmbeddedWebphoneUrl(snapshot.embeddedWebphoneUrl || '')
            setPauseCodeInput(snapshot.pauseCodeInput || '')
            setManualPhoneNumber(snapshot.manualPhoneNumber || '')
            setManualLeadId(snapshot.manualLeadId ?? null)
            setManualAutoDialPending(false)
            setDrawerState(snapshot.drawerState)
            setShow(true)
            setCollapsed(false)
          }
        } catch (error) {
          console.error('Unable to restore the standalone VICIdial drawer view', error)
        }
      }
    }

    const handleOpen = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyDrawerOpenDetail>
      if (!customEvent.detail?.url) return

      const payload = {...customEvent.detail}
      if (
        typeof window !== 'undefined' &&
        window.location.protocol === 'https:' &&
        payload.url.startsWith('http://')
      ) {
        payload.url = payload.url.replace(/^http:\/\//i, 'https://')
      }

      setEmbeddedWebphoneUrl('')
      setDrawerState(payload)
      setShow(true)
      setCollapsed(false)
    }

    const handleUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{diagnostics?: TelephonyDrawerDiagnostics}>
      if (!customEvent.detail?.diagnostics) return

      setDrawerState((current) => ({
        ...current,
        diagnostics: {
          ...(current.diagnostics || {}),
          ...customEvent.detail.diagnostics,
        },
      }))
    }

    const handleClose = () => {
      setEmbeddedWebphoneUrl('')
      setCollapsed(false)
      setShow(false)
    }

    const handleToggle = () => {
      if (!show) {
        if (drawerState.url) {
          setShow(true)
          setCollapsed(false)
        }
        return
      }

      setCollapsed((current) => !current)
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as {type?: string; webphoneUrl?: string} | undefined
      if (!data || data.type !== 'POLICYTOOLS_VICIDIAL_WEBPHONE_READY' || !data.webphoneUrl) {
        return
      }

      setEmbeddedWebphoneUrl(data.webphoneUrl)
      setDrawerState((current) => ({
        ...current,
        width: '460px',
        displayMode: 'webphone',
        diagnostics: {
          ...(current.diagnostics || {}),
          message: 'PolicyDial connected. Loading embedded webphone...',
        },
      }))
    }

    const handlePrepareDial = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyVicidialPrepareDialDetail>
      const phone = String(customEvent.detail?.phone || '').trim()
      if (!phone) return

      if (!telephony.vicidialSession.activeSession) {
        toast.error('Connect PolicyDial from Integrations before starting a call.')
        return
      }

      setManualPhoneNumber(phone)
      setManualLeadId(customEvent.detail?.leadId ?? null)
      setManualAutoDialPending(customEvent.detail?.autoDial !== false)
      setShow(true)
      setCollapsed(false)
    }

    window.addEventListener(TELEPHONY_DRAWER_OPEN_EVENT, handleOpen as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_UPDATE_EVENT, handleUpdate as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_CLOSE_EVENT, handleClose as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_TOGGLE_EVENT, handleToggle as EventListener)
    window.addEventListener(
      TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT,
      handlePrepareDial as EventListener
    )
    window.addEventListener('message', handleMessage)

    const channel = new BroadcastChannel('policytools_vicidial_channel')
    channel.onmessage = (event) => {
      if (event.data === 'CLOSE_WEBPHONE') {
        closeTrackedExternalWindow()
        setEmbeddedWebphoneUrl('')
        setCollapsed(false)
        setShow(false)
      }
    }

    return () => {
      window.removeEventListener(TELEPHONY_DRAWER_OPEN_EVENT, handleOpen as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_UPDATE_EVENT, handleUpdate as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_CLOSE_EVENT, handleClose as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_TOGGLE_EVENT, handleToggle as EventListener)
      window.removeEventListener(
        TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT,
        handlePrepareDial as EventListener
      )
      window.removeEventListener('message', handleMessage)
      channel.close()
    }
  }, [drawerState.provider, drawerState.url, show, telephony])

  useEffect(() => {
    publishDrawerState(show, show && !collapsed, drawerState.provider)
  }, [collapsed, drawerState.provider, publishDrawerState, show])

  useEffect(() => {
    const handleRequest = () => {
      publishDrawerState(show, show && !collapsed, drawerState.provider)
    }
    window.addEventListener(TELEPHONY_DRAWER_STATE_REQUEST_EVENT, handleRequest)
    return () => {
      window.removeEventListener(TELEPHONY_DRAWER_STATE_REQUEST_EVENT, handleRequest)
    }
  }, [collapsed, drawerState.provider, publishDrawerState, show])

  useEffect(() => {
    if (
      !manualAutoDialPending ||
      !manualPhoneNumber.trim() ||
      !telephony.vicidialSession.activeSession ||
      runningAction === 'dial'
    ) {
      return
    }

    setManualAutoDialPending(false)
    void handleManualCall()
  }, [
    handleManualCall,
    manualAutoDialPending,
    manualPhoneNumber,
    runningAction,
    telephony.vicidialSession.activeSession,
  ])

  const activeCallPhoneNumber = String(
    telephony.vicidialSession.phoneNumber || drawerState.diagnostics?.phone_number || ''
  ).trim()
  const activeCallVendorLeadCode = String(
    telephony.vicidialSession.vendorLeadCode || drawerState.diagnostics?.vendor_lead_code || ''
  ).trim()
  const activeCallLeadId = String(telephony.vicidialSession.leadId || '').trim()
  const activeCallSubStatus = String(
    telephony.vicidialSession.realTimeSubStatus ||
      drawerState.diagnostics?.real_time_sub_status ||
      ''
  )
    .trim()
    .toUpperCase()
  const effectiveVicidialStatus = useMemo(() => {
    const rawStatus = String(
      drawerState.diagnostics?.status || telephony.vicidialSession.status || ''
    )
      .trim()
      .toUpperCase()
    const activeCallId = String(
      telephony.vicidialSession.callId || drawerState.diagnostics?.call_id || ''
    ).trim()
    if (isDispositionPendingState(rawStatus, activeCallSubStatus)) {
      return 'DEAD'
    }
    if (
      rawStatus === 'QUEUE' &&
      (hasMeaningfulVicidialValue(activeCallId) ||
        activeCallSubStatus.includes('INCALL') ||
        activeCallSubStatus.includes('LIVE') ||
        activeCallSubStatus.includes('RING'))
    ) {
      return 'INCALL'
    }
    return rawStatus
  }, [
    activeCallSubStatus,
    drawerState.diagnostics?.call_id,
    drawerState.diagnostics?.status,
    telephony.vicidialSession.callId,
    telephony.vicidialSession.status,
  ])

  const isPausedState = effectiveVicidialStatus === 'PAUSED'
  const isReadyState =
    Boolean(telephony.vicidialSession.activeSession || drawerState.diagnostics?.session_id) &&
    !isPausedState &&
    effectiveVicidialStatus !== 'DEAD'
  const isPauseActionRunning = runningAction === 'pause'
  const isResumeActionRunning = runningAction === 'resume'
  // A logged-in agent who is PAUSED must always be able to resume ("DIAL NEXT
  // NUMBER"), even while the session marker is briefly re-connecting or
  // active_session momentarily reads false right after login (VICIdial reports
  // status=LOGIN for a few seconds before PAUSED, and PolicyTools agents have no
  // native screen to transition them). Previously requiring !connecting &&
  // activeSession left the button greyed out at login, stranding the agent.
  // Only hard-block during an explicit disconnect or an in-flight pause/resume.
  const canTogglePauseState =
    !disconnecting &&
    !runningAction &&
    (isPausedState || Boolean(telephony.vicidialSession.activeSession))
  // Forced pause reasons: the agent MUST pick one before pausing. Codes must
  // match vicidial_pause_codes for the campaign (BATH/LUNCH/SHORT/LONG).
  const pauseCodeOptions: Array<{code: string; label: string}> = [
    {code: 'BATH', label: 'Bathroom'},
    {code: 'LUNCH', label: 'Lunch'},
    {code: 'SHORT', label: 'Short break'},
    {code: 'LONG', label: 'Long break'},
  ]
  // Resuming ("DIAL NEXT NUMBER") needs no code; only pausing requires one.
  const pauseCodeRequiredButMissing = !isPausedState && !pauseCodeInput.trim()
  const pauseStatusLabel = isPausedState
    ? 'YOU ARE PAUSED'
    : isReadyState
    ? 'YOU ARE ACTIVE'
    : 'SESSION NOT READY'
  const pauseActionLabel = isPausedState ? 'DIAL NEXT NUMBER' : 'ACTIVE PAUSE'
  const pauseActionBusyLabel = isPausedState ? 'GOING ACTIVE...' : 'PAUSING...'
  const pauseActionVariant = isPausedState ? 'success' : 'warning'

  const hasStructuredDiagnostics = Boolean(
    drawerState.diagnostics?.agent_user ||
      drawerState.diagnostics?.status ||
      drawerState.diagnostics?.campaign_id ||
      drawerState.diagnostics?.pause_code ||
      drawerState.diagnostics?.session_id ||
      drawerState.diagnostics?.phone_number
  )
  const dispositionPending = isDispositionPendingState(effectiveVicidialStatus, activeCallSubStatus)
  const hasLiveCallContext =
    !dispositionPending &&
    (hasMeaningfulVicidialValue(
      telephony.vicidialSession.callId || drawerState.diagnostics?.call_id
    ) ||
      hasMeaningfulVicidialValue(activeCallLeadId) ||
      hasMeaningfulVicidialValue(activeCallPhoneNumber) ||
      hasMeaningfulVicidialValue(activeCallVendorLeadCode) ||
      ['INCALL', 'LIVE', 'QUEUE', 'RING'].some((marker) =>
        effectiveVicidialStatus.includes(marker)
      ) ||
      ['INCALL', 'LIVE', 'QUEUE', 'RING'].some((marker) => activeCallSubStatus.includes(marker)))
  const activeCallBannerTitle =
    effectiveVicidialStatus === 'INCALL' ||
    activeCallSubStatus.includes('INCALL') ||
    activeCallSubStatus.includes('LIVE')
      ? 'PolicyDial call active'
      : 'Incoming PolicyDial call'
  const activeConversationInProgress =
    effectiveVicidialStatus === 'INCALL' ||
    activeCallSubStatus.includes('INCALL') ||
    activeCallSubStatus.includes('LIVE')
  const selectedPhoneNumber = manualPhoneNumber.trim()
  const currentAgentUser = String(
    drawerState.diagnostics?.agent_user || telephony.vicidialSession.agentUser || 'Not assigned'
  ).trim()
  const currentCampaignId = String(
    drawerState.diagnostics?.campaign_id || telephony.vicidialSession.campaignId || 'Not assigned'
  ).trim()
  const currentSessionId = String(
    drawerState.diagnostics?.session_id || telephony.vicidialSession.sessionId || 'Pending'
  ).trim()
  const currentPauseCode = String(
    drawerState.diagnostics?.pause_code || telephony.vicidialSession.pauseCode || ''
  )
    .trim()
    .toUpperCase()
  const currentPhoneNumber = String(
    telephony.vicidialSession.phoneNumber || drawerState.diagnostics?.phone_number || ''
  ).trim()
  // The agent's own live connection quality (RTP), so they can see how good
  // their connection is. Null when there's no active media on their channel.
  const connectionAudio = telephony.vicidialSession.audio
  const connectionLossPct = connectionAudio
    ? Math.max(connectionAudio.recv_loss_pct, connectionAudio.send_loss_pct)
    : 0
  if (connectionAudio) {
    noMediaSinceRef.current = null
  } else if (noMediaSinceRef.current === null) {
    noMediaSinceRef.current = Date.now()
  }
  // Only warn after 60s of sustained no-media (the poll re-renders every few
  // seconds, so this flips on once the threshold passes).
  const showNoAudioConnection =
    !connectionAudio &&
    noMediaSinceRef.current !== null &&
    Date.now() - noMediaSinceRef.current >= 60000
  const callContextName = [
    String(telephony.vicidialSession.firstName || '').trim(),
    String(telephony.vicidialSession.lastName || '').trim(),
  ]
    .filter((value) => hasMeaningfulVicidialValue(value))
    .join(' ')
  const drawerTone = dispositionPending
    ? 'wrapup'
    : hasLiveCallContext
    ? 'live'
    : isPausedState
    ? 'paused'
    : isReadyState
    ? 'active'
    : 'idle'
  const canOpenDetachedWindow = Boolean(drawerState.url)
  const showUndockButton = canOpenDetachedWindow && drawerState.provider !== 'VICIDIAL'
  const policyToolsLookupPhone = useMemo(() => {
    const preferredPhone = hasLiveCallContext
      ? activeCallPhoneNumber
      : selectedPhoneNumber || drawerState.diagnostics?.phone_number || ''
    const normalizedPhone = normalizePhoneDigits(preferredPhone)
    return normalizedPhone.length >= 10 ? normalizedPhone : ''
  }, [
    activeCallPhoneNumber,
    drawerState.diagnostics?.phone_number,
    hasLiveCallContext,
    selectedPhoneNumber,
  ])
  const showPreparedDialInline =
    canOperateVicidial && !hasLiveCallContext && !dispositionPending && Boolean(selectedPhoneNumber)

  useEffect(() => {
    if (!show) {
      policyToolsLookupPhoneRef.current = ''
      setPolicyToolsContact(null)
      return
    }
    // Don't drop the contact when policyToolsLookupPhone goes blank for a tick
    // (status polls during live <-> idle transitions briefly return no phone).
    // The dedicated call-lifecycle effect below clears it on actual hangup.
    if (!policyToolsLookupPhone) return
    if (policyToolsLookupPhoneRef.current === policyToolsLookupPhone) return

    policyToolsLookupPhoneRef.current = policyToolsLookupPhone
    setPolicyToolsContact(null)
    let ignore = false

    lookupPolicyToolsContact({
      variables: {name: policyToolsLookupPhone},
      fetchPolicy: 'no-cache',
    })
      .then(({data}) => {
        if (ignore || policyToolsLookupPhoneRef.current !== policyToolsLookupPhone) return
        const contacts = (data?.allContactsPreview?.edges || [])
          .map((edge) => edge?.node)
          .filter(Boolean) as NodeContact[]
        setPolicyToolsContact(pickBestPolicyToolsContact(contacts, policyToolsLookupPhone))
      })
      .catch((error) => {
        if (ignore || policyToolsLookupPhoneRef.current !== policyToolsLookupPhone) return
        console.error('Error looking up PolicyTools contact for telephony drawer', error)
        setPolicyToolsContact(null)
      })

    return () => {
      ignore = true
    }
  }, [lookupPolicyToolsContact, policyToolsLookupPhone, show])

  const policyToolsLookupLeadId = useMemo(() => {
    const candidates = [activeCallVendorLeadCode, manualLeadId]
    for (const candidate of candidates) {
      const value = String(candidate || '').trim()
      if (hasMeaningfulVicidialValue(value)) return value
    }
    return ''
  }, [activeCallVendorLeadCode, manualLeadId])

  useEffect(() => {
    if (!show) {
      policyToolsLookupLeadIdRef.current = ''
      setPolicyToolsLead(null)
      return
    }
    // Same logic as the contact lookup above: keep the previous lead on screen
    // while the lookup id flickers empty. Call-lifecycle effect handles hangup.
    if (!policyToolsLookupLeadId) return
    if (policyToolsLookupLeadIdRef.current === policyToolsLookupLeadId) return

    policyToolsLookupLeadIdRef.current = policyToolsLookupLeadId
    setPolicyToolsLead(null)
    let ignore = false

    lookupPolicyToolsLead({
      variables: {id: policyToolsLookupLeadId},
      fetchPolicy: 'no-cache',
    })
      .then(({data}) => {
        if (ignore || policyToolsLookupLeadIdRef.current !== policyToolsLookupLeadId) return
        const lead = (data as {leadById?: typeof policyToolsLead})?.leadById || null
        setPolicyToolsLead(lead || null)
      })
      .catch((error) => {
        if (ignore || policyToolsLookupLeadIdRef.current !== policyToolsLookupLeadId) return
        console.error('Error looking up PolicyTools lead for telephony drawer', error)
        setPolicyToolsLead(null)
      })

    return () => {
      ignore = true
    }
  }, [lookupPolicyToolsLead, policyToolsLookupLeadId, show])

  const policyToolsLeadName = useMemo(() => {
    if (!policyToolsLead) return ''
    const fromContact = [
      String(policyToolsLead.contact?.firstName || '').trim(),
      String(policyToolsLead.contact?.lastName || '').trim(),
    ]
      .filter(Boolean)
      .join(' ')
      .trim()
    return fromContact || String(policyToolsLead.name || '').trim()
  }, [policyToolsLead])

  const policyToolsPolicyRows = useMemo(() => {
    if (!policyToolsContact) return []
    const seen = new Set<string>()
    const policies: MemberPolicySummary[] = []

    if (policyToolsContact.memberPolicy?.policy?.id) {
      seen.add(policyToolsContact.memberPolicy.policy.id)
      policies.push(policyToolsContact.memberPolicy)
    }

    for (const edge of policyToolsContact.members?.edges || []) {
      const memberPolicy = edge?.node
      const policyId = memberPolicy?.policy?.id
      if (!memberPolicy || !policyId || seen.has(policyId)) continue
      seen.add(policyId)
      policies.push(memberPolicy)
    }

    return policies
  }, [policyToolsContact])

  const policyToolsContactName =
    String(policyToolsContact?.fullName || '').trim() ||
    [
      String(policyToolsContact?.firstName || '').trim(),
      String(policyToolsContact?.lastName || '').trim(),
    ]
      .filter(Boolean)
      .join(' ')
  const policyToolsLocation = formatPolicyToolsLocation(policyToolsContact)
  const hasPolicyToolsContact = Boolean(
    policyToolsContact && phonesLikelyMatch(policyToolsContact.phone, policyToolsLookupPhone)
  )
  // Enriched contact fields for the Live Conversation grid (PolicyTools-only).
  const policyToolsPrimaryPolicy =
    policyToolsContact?.memberPolicy?.policy ||
    policyToolsPolicyRows.find((row) => row.policy)?.policy ||
    null
  const policyToolsAge = computePolicyToolsAge(policyToolsContact?.dateBirth)
  const policyToolsAgent = formatPolicyToolsPersonName(policyToolsPrimaryPolicy?.user)
  const policyToolsAssignedTo = formatPolicyToolsPersonName(policyToolsPrimaryPolicy?.assignedTo)
  const policyToolsHasActivePolicy = policyToolsPolicyRows.some(
    (row) => row.policy && !row.policy.isArchived
  )
  // Group the contact's policies by status with a count (avoids repeating the name).
  const policyToolsPolicyGroups = useMemo(() => {
    const groups = new Map<string, number>()
    for (const row of policyToolsPolicyRows) {
      const label = String(row.policy?.status || row.policy?.paymentStatus || '')
        .trim()
        .toUpperCase()
      if (!label) continue
      groups.set(label, (groups.get(label) || 0) + 1)
    }
    return Array.from(groups.entries()).map(([status, count]) => ({status, count}))
  }, [policyToolsPolicyRows])
  // When the call comes from a VICIdial auto-dial campaign, the lead's
  // first_name + last_name were uploaded into vicidial_list and travel down
  // through agent_status.lead_info. Prefer that authoritative source over the
  // PolicyTools-side phone match (which can collide with unrelated contacts
  // that happen to share the same number).
  const preferredCallContextName =
    callContextName ||
    (hasPolicyToolsContact && policyToolsContactName) ||
    policyToolsLeadName
  const activeCallElapsedLabel =
    liveCallStartedAt && activeConversationInProgress
      ? formatElapsedClock(nowTick - liveCallStartedAt)
      : ''
  const preparedDialHeadline = hasPolicyToolsContact
    ? policyToolsContactName || 'PolicyTools contact ready'
    : policyToolsLeadName ||
      (runningAction === 'dial' ? 'Calling through PolicyDial' : 'Selected number ready')
  const preparedDialPolicyLabels = policyToolsPolicyRows
    .map((memberPolicy) => buildPolicyToolsPolicyLabel(memberPolicy))
    .filter(Boolean)

  useEffect(() => {
    if (!selectedPhoneNumber) {
      preparedDialLifecycleSeenRef.current = false
      return
    }

    if (hasLiveCallContext || dispositionPending) {
      preparedDialLifecycleSeenRef.current = true
      return
    }

    if (!preparedDialLifecycleSeenRef.current) return

    preparedDialLifecycleSeenRef.current = false
    policyToolsLookupPhoneRef.current = ''
    setPolicyToolsContact(null)
    setManualPhoneNumber('')
    setManualLeadId(null)
  }, [dispositionPending, hasLiveCallContext, selectedPhoneNumber])

  // Clear the PolicyTools contact / lead panel when a call actually ends
  // (live + wrap-up both turn false), regardless of whether the user had
  // pre-selected a phone for the next dial. Replaces the previous behavior
  // of clearing on empty lookup phone, which made the info flicker out
  // during normal live <-> idle status polls.
  useEffect(() => {
    const inCall = hasLiveCallContext || dispositionPending
    if (inCall) {
      wasInCallRef.current = true
      return
    }
    if (!wasInCallRef.current) return
    wasInCallRef.current = false
    policyToolsLookupPhoneRef.current = ''
    policyToolsLookupLeadIdRef.current = ''
    setPolicyToolsContact(null)
    setPolicyToolsLead(null)
  }, [hasLiveCallContext, dispositionPending])

  // Single-owner guard: agents keep many PolicyTools tabs open; only one may run the
  // webphone or the SIP registrations fight (stolen contact / split audio). Other tabs
  // show a "running in another tab" panel instead of a second webphone.
  const webphoneActive = show && Boolean(drawerState.url)
  const webphoneInCall = Boolean(liveCallStartedAt)
  const webphoneOwnership = useWebphoneOwnership({active: webphoneActive, inCall: webphoneInCall})
  const webphoneBlockedByOtherTab = webphoneOwnership.ownerElsewhere && !webphoneOwnership.isOwner

  if (!show) return null

  return (
    <div
      role='dialog'
      aria-hidden={collapsed}
      className={`telephony-drawer telephony-drawer--${drawerTone}`}
      style={{
        width: drawerState.width || '460px',
        maxWidth: '100%',
        transform: collapsed ? 'translateX(100%)' : 'translateX(0)',
        opacity: collapsed ? 0 : 1,
        pointerEvents: collapsed ? 'none' : 'auto',
        visibility: collapsed ? 'hidden' : 'visible',
      }}
    >
      <div className='telephony-drawer__header'>
        <div className='telephony-drawer__header-actions'>
          <Button
            variant='light'
            size='sm'
            onClick={() => setShowAudioCheck(true)}
            className='telephony-drawer__ghost-button'
            title='Test your microphone and speaker'
          >
            <i className='bi bi-gear me-2' />
            Audio
          </Button>
          {showUndockButton && (
            <Button
              variant='light'
              size='sm'
              onClick={openInNewTab}
              disabled={disconnecting}
              className='telephony-drawer__ghost-button'
            >
              <i className='bi bi-box-arrow-up-right me-2' />
              Undock
            </Button>
          )}
          {drawerState.provider === 'VICIDIAL' && canOperateVicidial && (
            <Button
              variant='light'
              size='sm'
              onClick={() => void handleVicidialLogout()}
              disabled={disconnecting}
              className='telephony-drawer__ghost-button telephony-drawer__ghost-button--danger'
            >
              <i className='bi bi-box-arrow-right me-2' />
              {disconnecting ? 'Logging out...' : 'Logout'}
            </Button>
          )}
          <CloseButton
            onClick={() => setCollapsed(true)}
            aria-label='Close'
            className='telephony-drawer__close'
          />
        </div>
      </div>
      <div className='telephony-drawer__body'>
        {shouldForceExternalOpen && (
          <Alert variant='warning' className='telephony-drawer__alert'>
            <div className='fw-semibold mb-1'>Secure tab required</div>
            <div className='small'>
              Your current app session is running over HTTP. ViciPhone rejects embedded access from
              non-encrypted referrers like <code>localhost</code>.
            </div>
            <div className='mt-3'>
              <Button
                variant='warning'
                size='sm'
                onClick={openInNewTab}
                disabled={!canOpenDetachedWindow || disconnecting}
              >
                Open secure webphone
              </Button>
            </div>
          </Alert>
        )}
        {(drawerState.provider === 'VICIDIAL' || drawerState.diagnostics) && !isAgentScreenMode && (
          <section className='telephony-drawer__section telephony-drawer__section--hero'>
            <div className='telephony-drawer__hero-bar'>
              <div className='telephony-drawer__hero-copy'>
                <div className='telephony-drawer__section-kicker'>Session overview</div>
                <div className='telephony-drawer__hero-status-row'>
                  <span
                    className={`telephony-drawer__status-pill telephony-drawer__status-pill--${drawerTone}`}
                  >
                    {effectiveVicidialStatus || 'PENDING'}
                  </span>
                  <span className='telephony-drawer__hero-note'>{pauseStatusLabel}</span>
                </div>
              </div>
            </div>
            <div className='telephony-drawer__summary-grid telephony-drawer__summary-grid--compact'>
              <div className='telephony-drawer__summary-item'>
                <span className='telephony-drawer__summary-label'>Agent</span>
                <span className='telephony-drawer__summary-value'>{currentAgentUser}</span>
              </div>
              <div className='telephony-drawer__summary-item'>
                <span className='telephony-drawer__summary-label'>Campaign</span>
                <span className='telephony-drawer__summary-value'>{currentCampaignId}</span>
              </div>
              <div className='telephony-drawer__summary-item'>
                <span className='telephony-drawer__summary-label'>Session</span>
                <span className='telephony-drawer__summary-value telephony-drawer__summary-value--mono'>
                  {currentSessionId}
                </span>
              </div>
            </div>
            <div className='telephony-drawer__chip-row telephony-drawer__chip-row--tight'>
              {connectionAudio ? (
                <Badge
                  bg='light'
                  text='dark'
                  className='border telephony-drawer__chip d-inline-flex align-items-center gap-2'
                  title={`Your live connection quality — codec ${
                    connectionAudio.codec || 'n/a'
                  }, packet loss ${connectionLossPct}%, jitter ${connectionAudio.jitter_ms}ms. Green = good, yellow = fair, red = poor.`}
                >
                  <SignalBars quality={connectionAudio.quality} />
                  <span className='text-capitalize'>{connectionAudio.quality}</span>
                  <span className='text-muted'>
                    {connectionLossPct}% · {connectionAudio.jitter_ms}ms
                  </span>
                </Badge>
              ) : showNoAudioConnection ? (
                <span
                  className='badge border telephony-drawer__chip telephony-drawer__chip--no-audio d-inline-flex align-items-center gap-2'
                  title='No, it is not possible to establish an audio connection; please check your internet connection and try again.'
                >
                  <i className='bi bi-reception-0' />
                  No audio connection
                </span>
              ) : null}
              {currentPauseCode ? (
                <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                  Pause code: {currentPauseCode}
                </Badge>
              ) : null}
              {telephony.vicidialSession.callsInQueueCount > 0 ? (
                <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                  Queue: {telephony.vicidialSession.callsInQueueCount}
                </Badge>
              ) : null}
              {hasMeaningfulVicidialValue(currentPhoneNumber) &&
              !showPreparedDialInline &&
              !hasLiveCallContext ? (
                <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                  Phone: {currentPhoneNumber}
                </Badge>
              ) : null}
            </div>
            {showPreparedDialInline ? (
              <div className='telephony-drawer__inline-context'>
                <div className='telephony-drawer__inline-context-head'>
                  <span className='telephony-drawer__summary-label'>Dial target</span>
                  {hasPolicyToolsContact ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      PolicyTools contact
                    </Badge>
                  ) : null}
                </div>
                <div className='telephony-drawer__inline-context-title'>{preparedDialHeadline}</div>
                <div className='telephony-drawer__chip-row telephony-drawer__chip-row--tight'>
                  <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                    Phone: {selectedPhoneNumber}
                  </Badge>
                  {hasMeaningfulVicidialValue(policyToolsContact?.email) ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      Email: {policyToolsContact?.email}
                    </Badge>
                  ) : null}
                  {hasMeaningfulVicidialValue(policyToolsLocation) ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      Location: {policyToolsLocation}
                    </Badge>
                  ) : null}
                  {hasMeaningfulVicidialValue(policyToolsContact?.lead?.name) ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      Lead: {policyToolsContact?.lead?.name}
                    </Badge>
                  ) : null}
                  {!hasPolicyToolsContact && policyToolsLeadName ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      Lead: {policyToolsLeadName}
                    </Badge>
                  ) : !hasPolicyToolsContact && manualLeadId ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      Lead ID: {manualLeadId}
                    </Badge>
                  ) : null}
                  {preparedDialPolicyLabels[0] ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      {preparedDialPolicyLabels[0]}
                    </Badge>
                  ) : null}
                  {preparedDialPolicyLabels.length > 1 ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      +{preparedDialPolicyLabels.length - 1} more policies
                    </Badge>
                  ) : null}
                </div>
              </div>
            ) : null}
            {!hasStructuredDiagnostics && drawerState.diagnostics?.raw_response ? (
              <details className='telephony-drawer__details'>
                <summary>Advanced diagnostics</summary>
                <pre className='telephony-drawer__diagnostics-pre'>
                  {drawerState.diagnostics.raw_response}
                </pre>
              </details>
            ) : null}
          </section>
        )}
        {canOperateVicidial && hasLiveCallContext && (
          <section className='telephony-drawer__section telephony-drawer__section--call'>
            <div className='telephony-drawer__section-head'>
              <div>
                <div className='telephony-drawer__section-kicker'>Live conversation</div>
                <div className='telephony-drawer__section-title'>{activeCallBannerTitle}</div>
                {activeCallElapsedLabel ? (
                  <div className='telephony-drawer__call-timer' aria-live='polite'>
                    <span className='telephony-drawer__call-timer-label'>On call</span>
                    <span className='telephony-drawer__call-timer-value'>
                      {activeCallElapsedLabel}
                    </span>
                  </div>
                ) : null}
              </div>
              <Button
                variant='danger'
                size='sm'
                onClick={handleHangupCurrentCall}
                disabled={
                  disconnecting ||
                  telephony.vicidialSession.connecting ||
                  runningAction === 'hangup'
                }
                className='telephony-drawer__danger-button'
              >
                {runningAction === 'hangup' ? 'Hanging up...' : 'Hang up'}
              </Button>
            </div>
            {preferredCallContextName || hasPolicyToolsContact ? (
              <div className='telephony-drawer__lead-row'>
                <span className='telephony-drawer__lead-name'>
                  {preferredCallContextName || policyToolsContactName || 'Caller'}
                </span>
                {hasPolicyToolsContact ? (
                  <span className='telephony-drawer__source-tag telephony-drawer__source-tag--contact'>
                    PolicyTools contact
                  </span>
                ) : (
                  <span className='telephony-drawer__source-tag telephony-drawer__source-tag--lead'>
                    VICIdial lead
                  </span>
                )}
              </div>
            ) : null}
            <div className='telephony-drawer__info-grid'>
              {hasMeaningfulVicidialValue(activeCallPhoneNumber) ? (
                <div className='telephony-drawer__info-field'>
                  <span className='telephony-drawer__info-label'>Phone</span>
                  <span className='telephony-drawer__info-value telephony-drawer__info-value--mono'>
                    {activeCallPhoneNumber}
                  </span>
                </div>
              ) : null}
              {hasPolicyToolsContact ? (
                <>
                  {policyToolsAge !== null ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>Age</span>
                      <span className='telephony-drawer__info-value'>{policyToolsAge}</span>
                    </div>
                  ) : null}
                  <div className='telephony-drawer__info-field'>
                    <span className='telephony-drawer__info-label'>Active policy</span>
                    <span
                      className={`telephony-drawer__info-value${
                        policyToolsHasActivePolicy ? ' telephony-drawer__info-value--ok' : ''
                      }`}
                    >
                      {policyToolsHasActivePolicy ? 'Yes' : 'No'}
                    </span>
                  </div>
                  {hasMeaningfulVicidialValue(policyToolsLocation) ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>Location</span>
                      <span className='telephony-drawer__info-value'>{policyToolsLocation}</span>
                    </div>
                  ) : null}
                  {policyToolsAgent ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>Agent</span>
                      <span className='telephony-drawer__info-value'>{policyToolsAgent}</span>
                    </div>
                  ) : null}
                  {policyToolsAssignedTo ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>Assigned to</span>
                      <span className='telephony-drawer__info-value'>{policyToolsAssignedTo}</span>
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  {hasMeaningfulVicidialValue(activeCallLeadId) ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>Lead ID</span>
                      <span className='telephony-drawer__info-value telephony-drawer__info-value--mono'>
                        {activeCallLeadId}
                      </span>
                    </div>
                  ) : null}
                  {hasMeaningfulVicidialValue(activeCallVendorLeadCode) ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>Vendor ID</span>
                      <span className='telephony-drawer__info-value telephony-drawer__info-value--mono'>
                        {activeCallVendorLeadCode}
                      </span>
                    </div>
                  ) : null}
                </>
              )}
              {hasMeaningfulVicidialValue(
                telephony.vicidialSession.email || policyToolsContact?.email
              ) ? (
                <div className='telephony-drawer__info-field telephony-drawer__info-field--full'>
                  <span className='telephony-drawer__info-label'>Email</span>
                  <span className='telephony-drawer__info-value'>
                    {telephony.vicidialSession.email ||
                      (hasPolicyToolsContact ? policyToolsContact?.email : '')}
                  </span>
                </div>
              ) : null}
            </div>
            {hasMeaningfulVicidialValue(telephony.vicidialSession.comments) ? (
              <div
                className='border rounded p-2 mt-2 small'
                style={{ background: '#fff8e1', borderColor: '#ffd54f', whiteSpace: 'pre-wrap' }}
              >
                <span className='fw-bold me-1'>
                  <i className='bi bi-sticky me-1' />Notes:
                </span>
                {telephony.vicidialSession.comments}
              </div>
            ) : null}
            {hasPolicyToolsContact && policyToolsPolicyGroups.length > 0 ? (
              <div className='telephony-drawer__policies'>
                <span className='telephony-drawer__policies-label'>
                  Policies · {policyToolsPolicyRows.length}
                </span>
                <div className='telephony-drawer__policies-chips'>
                  {policyToolsPolicyGroups.map((group) => (
                    <span
                      key={group.status}
                      className={`telephony-drawer__policy-chip telephony-drawer__policy-chip--${policyToolsStatusTone(
                        group.status
                      )}`}
                    >
                      <span className='telephony-drawer__policy-count'>{group.count}</span>
                      {group.status}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {!hasPolicyToolsContact ? (
              <div className='telephony-drawer__not-found'>
                <strong>Not in PolicyTools.</strong> No contact or history.
              </div>
            ) : null}
          </section>
        )}
        {canOperateVicidial &&
          dispositionPending &&
          wrapUpStartedAt &&
          (() => {
            const elapsedSec = Math.max(0, Math.floor((nowTick - wrapUpStartedAt) / 1000))
            const minutes = Math.floor(elapsedSec / 60)
            const seconds = elapsedSec % 60
            return (
              <div className='telephony-drawer__wrapup-banner'>
                <i className='bi bi-hourglass-split text-warning' />
                <span className='fw-semibold small'>Wrap-up</span>
                <span className='fw-bold'>{`${String(minutes).padStart(2, '0')}:${String(
                  seconds
                ).padStart(2, '0')}`}</span>
                <span className='telephony-drawer__wrapup-note'>
                  Submit a disposition to release the call.
                </span>
              </div>
            )
          })()}
        {canOperateVicidial && (
          <section className='telephony-drawer__section telephony-drawer__section--controls'>
            <div className='telephony-drawer__section-head'>
              <div>
                <div className='telephony-drawer__section-kicker'>Agent controls</div>
                <div className='telephony-drawer__section-title'>Pause, resume, and transfer</div>
              </div>
              {telephony.vicidialSession.callsInQueueCount > 0 && (
                <Badge bg='info' className='text-uppercase telephony-drawer__queue-badge'>
                  <i className='bi bi-people-fill me-1' />
                  {telephony.vicidialSession.callsInQueueCount} in queue
                </Badge>
              )}
            </div>
            <div className='telephony-drawer__controls-row'>
              <div className='telephony-drawer__field-group'>
                <label className='telephony-drawer__field-label'>Pause reason</label>
                <Form.Select
                  size='sm'
                  value={pauseCodeInput}
                  onChange={(event) => setPauseCodeInput(event.target.value)}
                  disabled={disconnecting || telephony.vicidialSession.connecting || isPausedState}
                  className='telephony-drawer__input telephony-drawer__input--compact'
                >
                  <option value=''>Select a reason…</option>
                  {pauseCodeOptions.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <Button
                variant={pauseActionVariant}
                size='sm'
                onClick={isPausedState ? handleResumeAgent : handlePauseAgent}
                disabled={!canTogglePauseState || pauseCodeRequiredButMissing}
                className='telephony-drawer__primary-button'
              >
                {isPauseActionRunning || isResumeActionRunning
                  ? pauseActionBusyLabel
                  : pauseActionLabel}
              </Button>
            </div>
            <div className='telephony-drawer__field-group telephony-drawer__field-group--stretch'>
              <label className='telephony-drawer__field-label'>Blind transfer</label>
              <div className='telephony-drawer__inline-form'>
                <Form.Control
                  size='sm'
                  placeholder='Blind transfer number'
                  value={transferPhoneNumber}
                  onChange={(event) => setTransferPhoneNumber(event.target.value)}
                  disabled={disconnecting || telephony.vicidialSession.connecting}
                  className='telephony-drawer__input'
                />
                <Button
                  variant='outline-primary'
                  size='sm'
                  onClick={() => runVicidialDrawerAction('transfer', handleTransferCall)}
                  disabled={
                    disconnecting ||
                    telephony.vicidialSession.connecting ||
                    runningAction === 'transfer' ||
                    !telephony.vicidialSession.activeSession
                  }
                  className='telephony-drawer__secondary-button'
                >
                  {runningAction === 'transfer' ? 'Sending...' : 'Blind Transfer'}
                </Button>
              </div>
            </div>
          </section>
        )}
        {drawerState.url && !shouldForceExternalOpen ? (
          <section className='telephony-drawer__frame-section'>
            <div className='telephony-drawer__frame-toolbar'>
              <div className='telephony-drawer__frame-dots'>
                <span />
                <span />
                <span />
              </div>
              <div className='telephony-drawer__frame-label'>{drawerState.title || 'Webphone'}</div>
              <div className='telephony-drawer__frame-tag'>
                {embeddedWebphoneUrl && isVicidialAutologinBridge ? 'Embedded' : 'Live'}
              </div>
            </div>
            <div className='telephony-drawer__frame-shell'>
              {webphoneBlockedByOtherTab ? (
                <div className='telephony-drawer__other-tab'>
                  <div className='telephony-drawer__other-tab-icon'>
                    <i className='bi bi-window-stack' />
                  </div>
                  <div className='telephony-drawer__other-tab-title'>
                    PolicyDial is active in another tab
                  </div>
                  <div className='telephony-drawer__other-tab-copy'>
                    Your phone can only run in one tab at a time, otherwise calls collide. It is
                    running in another PolicyTools tab right now.
                  </div>
                  <Button
                    variant='primary'
                    size='sm'
                    disabled={webphoneOwnership.ownerInCall}
                    onClick={() => webphoneOwnership.requestTakeover()}
                  >
                    <i className='bi bi-arrow-left-right me-2' />
                    Use it here
                  </Button>
                  {webphoneOwnership.ownerInCall && (
                    <div className='telephony-drawer__other-tab-note'>
                      Finish the active call in the other tab first.
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <iframe
                    src={drawerState.url}
                    title={drawerState.title || 'Webphone'}
                    allow='microphone; autoplay'
                    referrerPolicy='no-referrer'
                    className={
                      embeddedWebphoneUrl && isVicidialAutologinBridge
                        ? 'telephony-drawer__iframe telephony-drawer__iframe--bridge-hidden'
                        : 'telephony-drawer__iframe'
                    }
                  />
                  {embeddedWebphoneUrl && isVicidialAutologinBridge ? (
                    <iframe
                      src={embeddedWebphoneUrl}
                      title='PolicyDial Embedded Webphone'
                      allow='microphone; autoplay'
                      referrerPolicy='no-referrer'
                      className='telephony-drawer__iframe telephony-drawer__iframe--embedded'
                    />
                  ) : null}
                </>
              )}
            </div>
          </section>
        ) : (
          <section className='telephony-drawer__frame-section telephony-drawer__frame-section--empty'>
            <div className='telephony-drawer__empty-state'>
              <div className='telephony-drawer__empty-icon'>
                <i className='bi bi-telephone-forward' />
              </div>
              <div className='telephony-drawer__empty-title'>Waiting for webphone</div>
              <div className='telephony-drawer__muted-copy'>
                Open or reconnect the telephony session to load the embedded workspace here.
              </div>
            </div>
          </section>
        )}
      </div>
      <AudioDeviceCheck show={showAudioCheck} onClose={() => setShowAudioCheck(false)} />
    </div>
  )
}
